import { BehaviorSubject, shareReplay, tap } from 'rxjs';
import { Translations } from 'src/common/models';
import { getCatalog } from 'src/utils/catalog';
import { origin } from 'src/utils/origin';
import { StorageUtil, TranslationUtils } from 'src/utils/storage.util';
import { APIError, HandleRequest } from './handle-request';
import { AddBasketLineItemsModel, AddLineItemModel, ApiResponse, BasketApi, BasketResponseModel, Configuration, ConfigurationParameters, LinesApi, OrderApi, ResponseError, ShippingMethodReturnModel, UpdateBasketAddressModel, UpdateShippingModel } from './openapi';
import { signal, computed } from '@angular/core';
import { CustomNotificationEvent } from 'src/common/events';



export namespace BasketApiMap {
  let _basket = new BehaviorSubject<BasketResponseModel | undefined>(undefined);
  let _loading = signal<boolean>(false)
  export let loading = computed(() => _loading());
  let basketId = '';
  let _apiconfig: ConfigurationParameters = {
    basePath: origin(),
  };
  let _config = new Configuration(_apiconfig);
  let catalog = getCatalog();
  let group = 'basket';
  let _basketApi = new BasketApi(_config);
  let _linesApi = new LinesApi(_config);
  let _orderApi = new OrderApi(_config);
  let _basketHandling: Partial<HandleRequest> = {
    loading: (loading) => _loading.set(loading),
    group,
    timeoutInMs: 5000,
    broadcast: true,
    debounce: 750,
    async onError<T>(context: ResponseError, init: HandleRequest) {
      console.error('context', context?.response)
      let group = (init as HandleRequest).group;
      let name = (init as HandleRequest).name;
      let status = typeof context == 'string' ? context : (context?.response?.status ?? (context?.cause as { message: string })?.message);
      let title = TranslationUtils.get(`${group}.${name}.${status}.title`.toUpperCase(), status.toString());
      let text = TranslationUtils.get(`${group}.${name}.${status}.text`.toUpperCase())
      // TODO fix
      CustomNotificationEvent.dispatch({
        title,
        text,
        color: 'var(--error-color, white)',
        backgroundColor: 'var(--error-background-color, red)',
        position: 'top-mid'
      })

      return { value: async () => null, raw: context.response } as ApiResponse<T>;
    },
  }
  if ('BroadcastChannel' in globalThis) {
    let _broadcastChannel = new BroadcastChannel(group);
    _broadcastChannel.onmessage = (msg: MessageEvent<BasketResponseModel>) => {
      if (msg?.data == null)
        return
      basketId = msg?.data?.basket?.basketId ?? '';

      _basket.next(msg.data as BasketResponseModel)
    };
  }
  export const basket$ = _basket.pipe(shareReplay(1));
  export function current() {
    return _basket.getValue();
  }
  export async function deleteBasket() {
    return await (await _basketApi.deleteBasket({ basketId, catalog }, _basketHandling));
  }
  export async function get() {
    return await (await _basketApi.get({ basketId, catalog }, _basketHandling));
  }
  export async function getExtras(method: ShippingMethodReturnModel): Promise<Array<{ id: string }>> {
    return []
    // return await (await _basketApi.getShippingMethodExtras({ catalog, basketId }, { debounce: 750, group: 'PaymentMethods' }));
  }
  export async function getPaymentMethods() {
    return await (await _basketApi.getPaymentMethods({ catalog, basketId }, { debounce: 750, group: 'PaymentMethods' })) ?? [];
  }
  export async function getPaymentUrl(paymentMethodId: string): Promise<string | undefined> {
    return await (await _basketApi.getPaymentUrl({ catalog, basketId, paymentMethodId }, {
      debounce: 750,
      group: 'PaymentUrl',
      timeoutInMs: 20000,
      retry: { maxRetries: 5, timeoutMs: 2000, excludedStatusCodes: [400, 401, 404, 500] },
      loading: (loading) => _loading.set(loading),
      async onError<T>(context: ResponseError, init: HandleRequest) {
        // throw new APIError("PAYMENT FAILED", context, init)
        console.error('context', context?.response)
        let group = (init as HandleRequest).group;
        let name = (init as HandleRequest).name;
        let status = typeof context == 'string' ? context : (context?.response?.status ?? (context?.cause as { message: string })?.message);
        let title = TranslationUtils.get(`${group}.${name}.${status}.title`.toUpperCase(), status.toString());
        let text = TranslationUtils.get(`${group}.${name}.${status}.text`.toUpperCase())
        // TODO fix
        CustomNotificationEvent.dispatch({
          title,
          text,
          color: 'var(--error-color, white)',
          backgroundColor: 'var(--error-background-color, red)',
          position: 'top-mid'
        })
        return { value: async () => undefined, raw: context.response } as ApiResponse<T>;
      }
    }));
  }
  export async function getPickupPoints(postalCode?: string, providerId?: string) {
    if (!providerId)
      return [];
    return await (await _basketApi.getPickupPoints({ catalog, basketId, postalCode, providerId }, { debounce: 750, group: 'PickupPoints' }));
  }
  export async function getShippingMethods() {
    return await (await _basketApi.getShippingMethods({ catalog, basketId }, { debounce: 750, group: 'ShippingMethods' }));
  }
  export async function setAcceptNewsletter(body: boolean) {
    if (!body)
      return
    return await (await _basketApi.setAcceptNewsletter({ basketId, catalog, body }, _basketHandling));
  }
  export async function setAcceptTerms(body: boolean) {
    return await (await _basketApi.setAcceptTerms({ basketId, catalog, body }, _basketHandling));
  }
  export async function setShipment(updateShippingModel: UpdateShippingModel) {
    return await (await _basketApi.setShipment({ basketId, catalog, updateShippingModel }, _basketHandling));
  }
  export async function updateAddresses(updateBasketAddressModel: UpdateBasketAddressModel) {
    return await (await _basketApi.updateAddresses({ basketId, catalog, updateBasketAddressModel }, _basketHandling));
  }
  export async function updateNote(body: string) {
    return await (await _basketApi.updateNote({ basketId, catalog, body }, _basketHandling));
  }
  export async function deleteLine(id: string[]) {
    return await (await _linesApi._delete({ basketId, catalog, id }, _basketHandling));
  }
  export async function createLine(addLineItemModel: AddLineItemModel) {
    return await (await _linesApi.createLine({ basketId, catalog, addLineItemModel }, { ..._basketHandling, id: addLineItemModel.sku }).then(() => {
      let title = TranslationUtils.get('CREATE_LINE.SUCCESS_TITLE');
      let text = TranslationUtils.get('CREATE_LINE.SUCCESS_TEXT');
      console.debug(title, text)
      CustomNotificationEvent.dispatch({
        backgroundColor: 'var(--success-background-color, #4a783d)',
        color: 'var(--success-color, #c7e7bd)',
        duration: 3000,
        title,
        text,
        position: 'top-right',
        icon: 'checkmark'
      })
    }));
  }
  export async function createLines(addBasketLineItemsModel: AddBasketLineItemsModel) {
    return await (await _linesApi.createLines({ basketId, catalog, addBasketLineItemsModel }, { ..._basketHandling, id: addBasketLineItemsModel.groupLine?.sku }).then(() => {
      let title = TranslationUtils.get('CREATE_LINES.SUCCESS_TITLE');
      let text = TranslationUtils.get('CREATE_LINES.SUCCESS_TEXT');
      console.debug(title, text)
      CustomNotificationEvent.dispatch({
        backgroundColor: 'var(--success-background-color, #4a783d)',
        color: 'var(--success-color, #c7e7bd)',
        duration: 3000,
        title,
        text,
        position: 'top-right',
        icon: 'checkmark'
      })
    }));
  }
  export async function updateOrCreateLine(addLineItemModel: AddLineItemModel) {
    return await (await _linesApi.updateOrCreateLine({ basketId, catalog, addLineItemModel }, { ..._basketHandling, id: addLineItemModel.sku }).then(() => {
      let title = TranslationUtils.get('UPDATE_LINE.SUCCESS_TITLE');
      let text = TranslationUtils.get('UPDATE_LINE.SUCCESS_TEXT');
      console.debug(title, text)
      CustomNotificationEvent.dispatch({
        backgroundColor: 'var(--success-background-color, #4a783d)',
        color: 'var(--success-color, #c7e7bd)',
        duration: 3000,
        title,
        text,
        position: 'top-right',
        icon: 'checkmark'
      })
    }));
  }
  export async function updateOrCreateLines(addBasketLineItemsModel: AddBasketLineItemsModel) {
    return await (await _linesApi.updateOrCreateLines({ basketId, catalog, addBasketLineItemsModel }, { ..._basketHandling, id: addBasketLineItemsModel.groupLine?.sku }).then(() => {
      let title = TranslationUtils.get('UPDATE_LINES.SUCCESS_TITLE');
      let text = TranslationUtils.get('UPDATE_LINES.SUCCESS_TEXT');
      console.debug(title, text)
      CustomNotificationEvent.dispatch({
        backgroundColor: 'var(--success-background-color, #4a783d)',
        color: 'var(--success-color, #c7e7bd)',
        duration: 3000,
        title,
        text,
        position: 'top-right',
        icon: 'checkmark'
      })
    }));
  }
  export async function getOrder(orderId: string) {
    return await (await _orderApi.getOrder({ orderId, skipTracking: false }, _basketHandling));
  }
}
